import { useAppDispatch, useShallowAppSelector } from '@/hooks';
import {
  goToNextImageViewerImage,
  goToPrevImageViewerImage,
  IMAGE_VIEWER_MAX_ZOOM,
  IMAGE_VIEWER_MIN_ZOOM,
  ImageViewerControls,
  ImageViewerPayload,
  initialImageViewerControls,
  InvertedDocumentMedium,
  patchImageViewerState,
  rotateImageViewer,
  selectImageViewerState,
  zoomImageViewer
} from '@/modules/ui';
import { useCallback, useEffect, useMemo } from 'react';

type OpenOptions = {
  images?: InvertedDocumentMedium[];
  openToIndex: number;
  controls?: 'reset' | ImageViewerControls;
};

type CloseOptions = {
  resetImages?: boolean;
  resetControls?: boolean;
};

export const useImageViewer = () => {
  const viewerState = useShallowAppSelector(selectImageViewerState);
  const dispatch = useAppDispatch();
  const currentStuff = useMemo(() => {
    const {
      images,
      currentIndex,
      controls: { zoom }
    } = viewerState;
    const hasImages = Array.isArray(images);
    const hasImageData =
      hasImages && images.length && currentIndex !== undefined;

    return {
      hasImages,
      currentImage: images?.[currentIndex ?? -1],
      hasPrevious: hasImageData && currentIndex > 0,
      hasNext: hasImageData && currentIndex < images.length - 1,
      canZoomIn: hasImageData && zoom < IMAGE_VIEWER_MAX_ZOOM,
      canZoomOut: hasImageData && zoom > IMAGE_VIEWER_MIN_ZOOM
    };
  }, [viewerState]);

  const openViewer = useCallback(
    (options?: OpenOptions) => {
      const payload: ImageViewerPayload = {
        open: true,
        currentIndex: options?.openToIndex ?? 0
      };

      if (options?.controls) {
        payload.controls =
          options.controls === 'reset'
            ? initialImageViewerControls
            : options.controls;
      }

      if (options?.images) {
        payload.images = options.images;
      }

      dispatch(patchImageViewerState(payload));
    },
    [dispatch]
  );

  const closeViewer = useCallback(
    (options?: CloseOptions) => {
      const payload: ImageViewerPayload = {
        open: false
      };
      if (options?.resetImages) {
        payload.images = undefined;
      }
      if (options?.resetControls) {
        payload.controls = initialImageViewerControls;
      }

      dispatch(patchImageViewerState(payload));
    },
    [dispatch]
  );

  const setImages = useCallback(
    (images?: InvertedDocumentMedium[]) => {
      dispatch(
        patchImageViewerState({
          images
        })
      );
    },
    [dispatch]
  );

  const zoomIn = useCallback(() => {
    dispatch(
      zoomImageViewer({
        direction: 'in'
      })
    );
  }, [dispatch]);

  const zoomOut = useCallback(() => {
    dispatch(
      zoomImageViewer({
        direction: 'out'
      })
    );
  }, [dispatch]);

  const resetZoom = useCallback(() => {
    dispatch(zoomImageViewer('reset'));
  }, [dispatch]);

  const rotateLeft = useCallback(() => {
    dispatch(rotateImageViewer('counterClockwise'));
  }, [dispatch]);

  const rotateRight = useCallback(() => {
    dispatch(rotateImageViewer('clockwise'));
  }, [dispatch]);

  const resetRotation = useCallback(() => {
    dispatch(rotateImageViewer('reset'));
  }, [dispatch]);

  const goToNext = useCallback(() => {
    dispatch(goToNextImageViewerImage());
  }, [dispatch]);

  const goToPrev = useCallback(() => {
    dispatch(goToPrevImageViewerImage());
  }, [dispatch]);

  useEffect(() => {
    const t = setTimeout(() => {
      if (viewerState.open && !viewerState.images?.length) {
        closeViewer({ resetControls: true, resetImages: true });
      }
    });
    return () => clearTimeout(t);
  }, [closeViewer, viewerState.images?.length, viewerState.open]);

  return {
    ...viewerState,
    ...currentStuff,
    openViewer,
    closeViewer,
    setImages,
    zoomIn,
    zoomOut,
    resetZoom,
    rotateLeft,
    rotateRight,
    resetRotation,
    goToNext,
    goToPrev
  };
};
